/**
 * Simple Observer Prototype
 *
 * (!) Asynchronous emit
 */
import { both, filter, generateUUID, promiseTry, propEq, reject, typeOf } from '~/utils/lib/core/prelude'
/**
 * @typedef {{
 *    subject: string;
 *    id: string;
 *    fn: function;
 *    args: any;
 * }} ObserverEntry
 */
/**
 * @constructor
 * @desc
 *
 *    Mode:
 *    - [ ] unchained
 *    - [x] chained
 *
 *    Execution:
 *    - [ ] sync
 *    - [x] Promise async
 *    - [ ] Function async
 */
export default function Observer() {
  /** @type {Array<ObserverEntry>} */
  this.subjects = []
}
/**
 * @function on
 * @constructs Observer.prototype#
 * @param {string} subject
 * @param {function} fn
 * @param {*} [args]
 * @return {ObserverEntry["id"]}
 */
Observer.prototype.on = function(subject, fn, args) {
  const id = generateUUID()
  this.subjects.push({ subject, fn, args, id })
  return id
}
/**
 * @function off
 * @desc Heads-up: as JavaScript function equality bears inherent limitation,
 *    using "id" unsubscription is necessary when re-using same "stringified"
 *    function (see code source)
 * @example
 *    observer.off('foo') // delete all foo subjects
 *
 *    observer.on('foo', myFunc)
 *    observer.off('foo', myFunc) // delete only subject using `myFunc`
 *
 *    const id = observer.on('foo', myFuncUsedEverywhere)
 *    observer.off('foo', id) // delete one and only subject entry
 *
 * @constructs Observer.prototype#
 * @template {ObserverEntry["fn"]|ObserverEntry["id"]|undefined} T
 * @param {string} subject
 * @param {T} [offArg=undefined]
 * @return {void}
 */
Observer.prototype.off = function(subject, offArg = undefined) {
  switch (typeOf(offArg)) {

    case 'function': {
      // @ts-ignore: UNSAFE coerce
      const /** @type {function} */ fn = offArg
      // (JavaScript <function> equality limitation)
      const sameFn = (/** @type {ObserverEntry} */ obj) => obj.fn.toString() === fn.toString()
      const sameSubject = propEq('subject', subject)
      const predicate = both(sameSubject, sameFn)

      this.subjects = reject(predicate)(this.subjects)

      break
    }

    case 'string': {
      // @ts-ignore: UNSAFE coerce
      const /** @type {string} */ id = offArg
      const sameSubject = propEq('subject', subject)
      const sameId = propEq('id', id)
      const predicate = both(sameSubject, sameId)

      this.subjects = reject(predicate, this.subjects)

      break
    }

    default: {
      const predicate = propEq('subject', subject)

      this.subjects = reject(predicate)(this.subjects)

      break
    }
  }
}
/**
 * @function emit
 * @param {string} subject
 * @param {*} [data] - data to pass to subscribers
 */
Observer.prototype.emit = function(subject, data) {
  const targetedSubjects = filter(
    propEq('subject', subject)
  )(this.subjects)

  const promises = targetedSubjects.map(
    item => promiseTry(
      () => item.fn.call(this, data)
    )
  )

  return Promise.all(promises)
}
